/**
 * City registry for subdomain-based multi-city routing.
 *
 * Each city runs at its own subdomain: canberra.pulseboard.com.au
 * The city slug is derived from the subdomain at runtime.
 * VITE_DEFAULT_CITY_SLUG is used for local dev (localhost has no subdomain).
 *
 * Kept in sync with the `cities` seed in apps/api/drizzle/998_seed.sql.
 */
export interface City {
  slug: string;
  name: string;
  state: "ACT" | "NSW" | "VIC" | "QLD" | "WA" | "SA" | "TAS" | "NT";
  lat: number;
  lng: number;
  timezone: string;
  isActive: boolean;
}

export const CITIES: readonly City[] = [
  {
    slug: "canberra",
    name: "Canberra",
    state: "ACT",
    lat: -35.2809,
    lng: 149.13,
    timezone: "Australia/Sydney",
    isActive: true,
  },
  {
    slug: "sydney",
    name: "Sydney",
    state: "NSW",
    lat: -33.8688,
    lng: 151.2093,
    timezone: "Australia/Sydney",
    isActive: false,
  },
  {
    slug: "melbourne",
    name: "Melbourne",
    state: "VIC",
    lat: -37.8136,
    lng: 144.9631,
    timezone: "Australia/Melbourne",
    isActive: false,
  },
  {
    slug: "brisbane",
    name: "Brisbane",
    state: "QLD",
    lat: -27.4698,
    lng: 153.0251,
    timezone: "Australia/Brisbane",
    isActive: false,
  },
  {
    slug: "perth",
    name: "Perth",
    state: "WA",
    lat: -31.9523,
    lng: 115.8613,
    timezone: "Australia/Perth",
    isActive: false,
  },
  {
    slug: "adelaide",
    name: "Adelaide",
    state: "SA",
    lat: -34.9285,
    lng: 138.6007,
    timezone: "Australia/Adelaide",
    isActive: false,
  },
  {
    slug: "hobart",
    name: "Hobart",
    state: "TAS",
    lat: -42.8821,
    lng: 147.3272,
    timezone: "Australia/Hobart",
    isActive: false,
  },
  {
    slug: "darwin",
    name: "Darwin",
    state: "NT",
    lat: -12.4634,
    lng: 130.8456,
    timezone: "Australia/Darwin",
    isActive: false,
  },
] as const;

export const DEFAULT_CITY_SLUG =
  (import.meta.env.VITE_DEFAULT_CITY_SLUG as string | undefined) ?? "canberra";

export function findCity(slug: string | undefined): City | undefined {
  if (!slug) return undefined;
  return CITIES.find((c) => c.slug === slug.toLowerCase());
}

export function isKnownCitySlug(slug: string | undefined): boolean {
  return !!findCity(slug);
}

/**
 * Resolve the current city slug from the subdomain, falling back to the
 * VITE_DEFAULT_CITY_SLUG env var (used for local dev where there's no subdomain).
 *
 * canberra.pulseboard.com.au → "canberra"
 * localhost / unknown subdomain → VITE_DEFAULT_CITY_SLUG ?? "canberra"
 */
export function getCitySlug(): string {
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";
  const [subdomain] = hostname.split(".");
  if (subdomain && isKnownCitySlug(subdomain)) return subdomain.toLowerCase();
  return DEFAULT_CITY_SLUG;
}
